import { parsePhoneNumber } from "libphonenumber-js";

export const PhoneInputHelper = (function () {
  const config = {
    wrapper: ".phone-input-wrapper",
    dropdown: ".phone-input-dropdown",
    dropdownToggle: ".phone-input-btn",
    dropdownMenu: ".phone-input-dropdown-menu",
    dropdownItem: ".dropdown-item",
    input: ".phone-input",
    hiddenInput: ".phone-input-hidden",
  };

  const phoneInputs = [];
  const staticUrl = document.querySelector(config.wrapper)?.dataset?.staticUrl;
  const countryCodes = frontendVars.countries;

  const init = function () {
    const phoneInputWrappers = document.querySelectorAll(config.wrapper);

    if (!phoneInputWrappers.length) {
      return;
    }

    phoneInputWrappers.forEach((wrapper) => {
      const phoneInput = {
        wrapper,
        dropdown: wrapper.querySelector(config.dropdown),
        dropdownToggle: wrapper.querySelector(config.dropdownToggle),
        dropdownMenu: wrapper.querySelector(config.dropdownMenu),
        input: wrapper.querySelector(config.input),
        hiddenInput: wrapper.querySelector(config.hiddenInput),
        activeCountry: getCountryBySiteViewId(frontendVars.siteviewId),
        dropdownOpen: false,
      };

      phoneInputs.push(phoneInput);
    });

    phoneInputs.forEach((phoneInput) => initializePhoneInput(phoneInput));
  };

  const generateCountryDropdownItems = (dropdownMenu) => {
    countryCodes.forEach((code) => {
      const callingCode = getCallingCode(code);

      if (callingCode) {
        const countryWrapper = document.createElement("div");
        countryWrapper.classList.add("dropdown-item");
        countryWrapper.setAttribute("data-country-code", code);

        const countryFlag = document.createElement("img");
        countryFlag.setAttribute("alt", code);
        countryFlag.setAttribute(
          "src",
          `${staticUrl}/${code.toLowerCase()}.svg`,
        );

        const countryCallingCode = document.createElement("span");
        countryCallingCode.innerText = `+${callingCode}`;

        countryWrapper.appendChild(countryFlag);
        countryWrapper.appendChild(countryCallingCode);

        dropdownMenu.appendChild(countryWrapper);

        countryWrapper.addEventListener("click", dropdownMenuItemClick);
      }
    });
  };

  const initializePhoneInput = (phoneInput) => {
    const { dropdownMenu, dropdownToggle, input, hiddenInput, activeCountry } =
      phoneInput;

    // Add country dropdown items
    generateCountryDropdownItems(dropdownMenu);

    // Add event listeners
    dropdownToggle.addEventListener("click", dropdownMenuClick);
    input.addEventListener("input", setInputValue);
    document.addEventListener("click", dropdownMenuClose);

    phoneInput.wrapper.setAttribute("data-country-code", activeCountry);

    const value = hiddenInput.value;
    if (value !== "") {
      phoneInput.input.value = value;

      // Detect country from existing phone number
      setInputValue(phoneInput);

      if (!value.startsWith("00") && !value.startsWith("+")) {
        setActiveCountry(phoneInput, activeCountry);
      }
    } else {
      // Change country to current frontend vars country code
      setActiveCountry(phoneInput, activeCountry);
    }
  };

  const getPhoneNumberDetails = (phoneNr, options) => {
    try {
      if (options["countryCode"]) {
        return parsePhoneNumber(phoneNr, options["countryCode"]);
      } else {
        return parsePhoneNumber(phoneNr);
      }
    } catch {
      return null;
    }
  };

  const findPhoneInput = (object) => {
    return phoneInputs.find(
      (phoneInput) =>
        phoneInput.wrapper ===
        (object.target ? object.target : object).closest(config.wrapper),
    );
  };

  // The object can be either an event from an event listener
  // or it can be the phoneInput elements object listed in the phoneInputs array
  const setInputValue = (object) => {
    const phoneInput = object.target ? findPhoneInput(object) : object;

    const nationalPhoneNumber = formatInternationalNumber(
      phoneInput.input,
      phoneInput.activeCountry,
    );
    phoneInput.input.value = nationalPhoneNumber;

    const internationalPhoneNumber = formatNationalNumber(
      phoneInput.input,
      phoneInput.activeCountry,
    );
    phoneInput.hiddenInput.value = internationalPhoneNumber;
  };

  // The object can be either an event from an event listener
  // or it can be the phoneInput elements object listed in the phoneInputs array
  const setActiveCountry = (object, countryCode) => {
    const phoneInput = object.target ? findPhoneInput(object) : object;

    const updatedPhoneInput = { ...phoneInput, activeCountry: countryCode };
    const phoneInputIndex = phoneInputs.indexOf(phoneInput);

    phoneInputs.splice(phoneInputIndex, 1, updatedPhoneInput);

    phoneInput.wrapper.setAttribute("data-country-code", countryCode);

    const activeCountryFlag = phoneInput.dropdownToggle.querySelector("img");
    const activeCountryCallCode =
      phoneInput.dropdownToggle.querySelector("span");

    // Remove current active class from old item
    phoneInput.dropdownMenu
      .querySelector(".active")
      ?.classList?.remove("active");

    // Add the active class to the newly selected item
    const dropdownItem = phoneInput.wrapper.querySelector(
      `[data-country-code=${countryCode}]`,
    );
    dropdownItem?.classList?.add("active");

    // Update the dropdownToggle button content to represent newly selected country
    const callingCode = getCallingCode(countryCode);

    if (callingCode) {
      activeCountryFlag.setAttribute(
        "src",
        `${staticUrl}/${countryCode.toLowerCase()}.svg`,
      );
      activeCountryFlag.setAttribute("alt", countryCode);

      activeCountryCallCode.innerText = `+${callingCode}`;
    }

    setInputValue(updatedPhoneInput);
  };

  const formatInternationalNumber = (input, countryCode) => {
    const value = input.value ? input.value : "";
    const phoneNumber = getPhoneNumberDetails(value, { countryCode });

    if (!phoneNumber) {
      return value;
    }

    if (
      phoneNumber &&
      countryCode?.toLowerCase() !== phoneNumber?.country?.toLowerCase()
    ) {
      setActiveCountry(findPhoneInput(input), phoneNumber.country);
    }

    return phoneNumber.nationalNumber;
  };

  const formatNationalNumber = (input, countryCode) => {
    const phoneNumber = getPhoneNumberDetails(input.value, { countryCode });

    return phoneNumber ? phoneNumber.number : input.value;
  };

  const getCallingCode = (countryCode) => {
    try {
      // 111 represents dummy phone number because phone number cannot be empty
      const phoneNr = parsePhoneNumber("111", countryCode);
      return phoneNr.countryCallingCode;
    } catch {
      return null;
    }
  };

  const dropdownMenuClose = (event) => {
    const openDropdownPhoneInputs = phoneInputs.filter(
      (phoneInput) => phoneInput.dropdownOpen === true,
    );

    // Loop over all the phone inputs with open dropdowns and close the ones which arent clicked on
    openDropdownPhoneInputs.forEach((phoneInput) => {
      const isClickInside =
        phoneInput.dropdownMenu.contains(event.target) ||
        phoneInput.dropdownToggle.contains(event.target);

      if (!isClickInside) {
        dropdownMenuClick(phoneInput);
      }
    });
  };

  const dropdownMenuClick = (object) => {
    const phoneInput = object.target ? findPhoneInput(object) : object;
    const open = phoneInput.dropdownMenu.classList.toggle("show");

    const updatedPhoneInput = { ...phoneInput, dropdownOpen: open };
    const phoneInputIndex = phoneInputs.indexOf(phoneInput);

    phoneInputs.splice(phoneInputIndex, 1, updatedPhoneInput);
  };

  const dropdownMenuItemClick = (event) => {
    const phoneInput = findPhoneInput(event);
    const dropdownItem = event.target.closest(".dropdown-item");
    const countryCode = dropdownItem.dataset.countryCode;

    setActiveCountry(phoneInput, countryCode);
    dropdownMenuClick(event);

    const triggerEvent = new Event("change");
    phoneInput.input.dispatchEvent(triggerEvent);
  };

  /**
   * Returns the country code based on the provided site view ID.
   *
   * @param {string} siteViewId - The site view ID to determine the country code.
   * @returns {string} The country code corresponding to the site view ID.
   */
  const getCountryBySiteViewId = (siteViewId) => {
    switch (siteViewId) {
      case "1":
        return "NL";
      case "2":
        return "BE";
      case "4":
      case "7":
        return "FR";
      default:
        return "NL";
    }
  };

  return {
    init: init,
  };
})();
